// src/components/orders/PaymentMethodSelector.js
import React from 'react';
import { CreditCard, Truck, Clock, Percent, CheckCircle } from 'lucide-react';

const PaymentMethodSelector = ({ 
  selectedPaymentMethod, 
  onPaymentMethodChange, 
  subtotal,
  className = ""
}) => {
  const paymentMethods = [
    {
      id: 'contrassegno',
      name: 'Contrassegno',
      description: 'Pagamento alla consegna',
      icon: Truck,
      discount: 0,
      color: 'gray',
      details: 'Paga quando ricevi la merce'
    },
    {
      id: 'consegna',
      name: 'Alla Consegna',
      description: 'Sconto 2%',
      icon: Clock,
      discount: 2,
      color: 'blue',
      details: 'Pagamento immediato alla consegna'
    },
    {
      id: 'anticipato',
      name: 'Anticipato',
      description: 'Sconto 3%',
      icon: CreditCard,
      discount: 3,
      color: 'green',
      details: 'Pagamento prima della spedizione'
    }
  ];

  const calculateDiscount = (method) => {
    return (subtotal * method.discount) / 100;
  };

  const calculateTotal = (method) => {
    return subtotal - calculateDiscount(method);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
          <CreditCard className="w-5 h-5 mr-2" style={{color: '#c48f75'}} />
          Modalità di Pagamento
        </h3>
        <p className="text-sm text-gray-600">
          Seleziona il metodo di pagamento preferito
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {paymentMethods.map((method) => {
          const isSelected = selectedPaymentMethod === method.id;
          const discount = calculateDiscount(method);
          const total = calculateTotal(method);
          const Icon = method.icon;

          return (
            <div key={method.id} className="relative">
              <button
                onClick={() => onPaymentMethodChange(method.id)}
                className={`w-full p-5 border-2 rounded-xl transition-all duration-200 text-left hover:shadow-lg ${
                  isSelected
                    ? 'shadow-lg ring-2'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                style={isSelected ? {
                  backgroundColor: '#fcfbd6',
                  borderColor: '#c48f75',
                  ringColor: '#c48f75'
                } : {}}
              >
                {/* Header con icona e titolo */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected 
                        ? '' 
                        : 'bg-gray-100'
                    }`}
                    style={isSelected ? {backgroundColor: '#c48f75'} : {}}>
                      <Icon className={`w-5 h-5 ${
                        isSelected 
                          ? 'text-white' 
                          : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{method.name}</h4>
                      {method.discount > 0 && (
                        <div className="flex items-center space-x-1">
                          <Percent className="w-3 h-3" style={{color: '#c48f75'}} />
                          <span className="text-sm font-medium" style={{color: '#c48f75'}}>
                            -{method.discount}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isSelected && (
                    <CheckCircle className="w-6 h-6" style={{color: '#c48f75'}} />
                  )}
                </div>

                {/* Descrizione */}
                <p className="text-sm text-gray-600 mb-4">
                  {method.details}
                </p>

                {/* Calcoli */}
                <div className="space-y-2 border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotale:</span>
                    <span className="font-medium">€{subtotal.toFixed(2)}</span>
                  </div>
                  
                  {method.discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span style={{color: '#c48f75'}}>Sconto ({method.discount}%):</span>
                      <span className="font-medium" style={{color: '#c48f75'}}>-€{discount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-base font-semibold border-t border-gray-200 pt-2">
                    <span>Totale:</span>
                    <span className={`${method.discount > 0 ? '' : 'text-gray-900'}`}
                    style={method.discount > 0 ? {color: '#c48f75'} : {}}>
                      €{total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </button>

              {/* Badge sconto */}
              {method.discount > 0 && (
                <div className="absolute -top-2 -right-2 text-white text-xs font-bold px-2 py-1 rounded-full"
                style={{backgroundColor: '#c48f75'}}>
                  -{method.discount}%
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Riepilogo selezionato */}
      {selectedPaymentMethod && (
        <div className="mt-6 p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">
                Metodo selezionato: {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
              </h4>
              <p className="text-sm text-gray-600">
                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.details}
              </p>
            </div>
            <div className="text-right">
              {paymentMethods.find(m => m.id === selectedPaymentMethod)?.discount > 0 && (
                <div className="text-sm font-medium" style={{color: '#c48f75'}}>
                  Risparmio: €{calculateDiscount(paymentMethods.find(m => m.id === selectedPaymentMethod)).toFixed(2)}
                </div>
              )}
              <div className="text-lg font-bold text-gray-900">
                Totale: €{calculateTotal(paymentMethods.find(m => m.id === selectedPaymentMethod)).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSelector;